import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { useCallback, useContext, useMemo } from 'react';

import FilesQueryContext, {
  FilesQueryFilters,
} from '~pages/Approvals/Files/FilesQueryContext';

interface QuickFilterProps<TFilterKey extends keyof FilesQueryFilters> {
  label: string;
  filterKey: TFilterKey;
  filterValue: FilesQueryFilters[TFilterKey];
  onActivate?: () => void;
}

export default function QuickFilter<TFilterKey extends keyof FilesQueryFilters>({
  label,
  filterKey,
  filterValue,
  onActivate,
}: QuickFilterProps<TFilterKey>) {
  const theme = useTheme();
  const filesQuery = useContext(FilesQueryContext);

  const isActive = useMemo(() => {
    const currentValue = filesQuery.filters[filterKey];
    if (Array.isArray(currentValue) && Array.isArray(filterValue)) {
      return JSON.stringify(currentValue) === JSON.stringify(filterValue);
    }
    return currentValue === filterValue;
  }, [filesQuery.filters, filterKey, filterValue]);

  const handleClick = useCallback(() => {
    const nextFilterValue = isActive ? undefined : filterValue;

    if (!isActive) {
      onActivate?.();
    }

    filesQuery.applyFilters({
      [filterKey]: nextFilterValue,
    } as Partial<Pick<FilesQueryFilters, TFilterKey>>);
  }, [isActive, filterKey, filterValue, onActivate, filesQuery.applyFilters]);

  return (
    <Box
      onClick={handleClick}
      sx={{
        alignItems: 'center',
        bgcolor: isActive
          ? theme.brandV2.colors.treadGray7
          : theme.semantic.surfaceRaised,
        border: `solid 1px ${isActive ? theme.brandV2.colors.treadGray5 : theme.brandV2.colors.treadGray7}`,
        display: 'flex',
        px: 1.2,
        py: 0.5,
        borderRadius: '48px',
        cursor: 'pointer',
      }}
    >
      <Typography variant="body1" sx={{ textAlign: 'center' }}>
        {label}
      </Typography>
    </Box>
  );
}
